export const RTE_CAPTION_KIND_ATTR = "data-rte-caption-kind";
export const RTE_CAPTION_ABBR_ATTR = "data-rte-caption-abbr";
export const RTE_CAPTION_ALIGN_ATTR = "data-rte-caption-align";
export const RTE_CAPTION_SIZE_ATTR = "data-rte-caption-size";
export const RTE_CAPTION_CUSTOM_ATTR = "data-rte-caption-custom";

export const CAPTION_SIZES = [
  { id: "12px", label: "Small" },
  { id: "14px", label: "Medium" },
  { id: "16px", label: "Large" },
];

export const CAPTION_LABELS = {
  image: { full: "Image", abbr: "Img." },
  figure: { full: "Figure", abbr: "Fig." },
  table: { full: "Table", abbr: "Tbl." },
  slides: { full: "Slide", abbr: "Sld." },
};

export const captionSizeOf = (value) => {
  const raw = String(value || "").trim();
  if (/^\d+(?:\.\d+)?px$/i.test(raw)) return raw;
  return "14px";
};

const CAPTION_CATALOG_CLASS = "rte-caption-catalog";

const addUniqueCaptionLabel = (labels, seen, text) => {
  const word = String(text || "").trim();
  const key = word.toLowerCase();
  if (!word || seen.has(key)) return;
  seen.add(key);
  labels.push(word);
};

/** Custom labels saved in this document, plus any still on a caption. */
export function customCaptionLabels(root) {
  const labels = [];
  const seen = new Set();
  if (!root?.querySelectorAll) return labels;
  const catalog = root.querySelector(`:scope > .${CAPTION_CATALOG_CLASS}`);
  String(catalog?.getAttribute("data-labels") || "")
    .split("\n")
    .forEach((text) => addUniqueCaptionLabel(labels, seen, text));
  root.querySelectorAll(`[${RTE_CAPTION_CUSTOM_ATTR}]`).forEach((el) => {
    addUniqueCaptionLabel(labels, seen, el.getAttribute(RTE_CAPTION_CUSTOM_ATTR));
  });
  return labels;
}

export function rememberCustomCaptionLabel(root, text) {
  const word = String(text || "").trim();
  if (!word || !root?.ownerDocument) return;
  const labels = customCaptionLabels(root);
  addUniqueCaptionLabel(labels, new Set(labels.map((label) => label.toLowerCase())), word);
  let catalog = root.querySelector(`:scope > .${CAPTION_CATALOG_CLASS}`);
  if (!catalog) {
    catalog = root.ownerDocument.createElement("div");
    catalog.className = CAPTION_CATALOG_CLASS;
    catalog.setAttribute("contenteditable", "false");
    catalog.setAttribute("aria-hidden", "true");
    catalog.hidden = true;
    root.appendChild(catalog);
  }
  const next = labels.join("\n");
  if (catalog.getAttribute("data-labels") !== next) catalog.setAttribute("data-labels", next);
}

export function defaultCaptionKind(node) {
  if (!node) return "figure";
  if (node.tagName === "TABLE" || node.classList?.contains("rte-table-figure")) return "table";
  if (node.classList?.contains("rte-flow")) return "figure";
  return "image";
}

export function captionHostFor(node) {
  if (!node) return null;
  if (node.classList?.contains("rte-table-figure")) return node;
  if (node.tagName === "TABLE") return node.closest("figure.rte-table-figure") || node;
  return node;
}

export function readCaptionSettings(node) {
  const host = captionHostFor(node);
  const has = !!host?.querySelector?.(":scope > figcaption.rte-caption");
  const kind = host?.getAttribute?.(RTE_CAPTION_KIND_ATTR) || defaultCaptionKind(host);
  const abbr = host?.getAttribute?.(RTE_CAPTION_ABBR_ATTR) === "1";
  const align = ["left", "center", "right"].includes(host?.getAttribute?.(RTE_CAPTION_ALIGN_ATTR))
    ? host.getAttribute(RTE_CAPTION_ALIGN_ATTR)
    : "center";
  const size = captionSizeOf(host?.getAttribute?.(RTE_CAPTION_SIZE_ATTR));
  const custom = host?.getAttribute?.(RTE_CAPTION_CUSTOM_ATTR) || "";
  return { has, kind, abbr, align, size, custom, host };
}

export function ensureTableCaptionFigure(table) {
  const existing = table.closest("figure.rte-table-figure");
  if (existing) return existing;
  const doc = table.ownerDocument;
  const fig = doc.createElement("figure");
  fig.className = "rte-table-figure";
  fig.setAttribute("contenteditable", "false");
  fig.draggable = false;
  table.setAttribute("contenteditable", "true");
  table.draggable = false;
  table.replaceWith(fig);
  fig.appendChild(table);
  return fig;
}

export function unwrapTableCaptionFigure(fig) {
  const table = fig.querySelector(":scope > table");
  if (!table) {
    fig.remove();
    return null;
  }
  table.removeAttribute("contenteditable");
  fig.replaceWith(table);
  return table;
}

function ensureCaptionElement(fig) {
  const doc = fig.ownerDocument;
  let cap = fig.querySelector(":scope > figcaption.rte-caption");
  if (!cap) {
    cap = doc.createElement("figcaption");
    cap.className = "rte-caption";
    cap.setAttribute("contenteditable", "false");
    fig.appendChild(cap);
  }
  let label = cap.querySelector(":scope > .rte-caption-label");
  if (!label) {
    label = doc.createElement("span");
    label.className = "rte-caption-label";
    label.setAttribute("contenteditable", "false");
    cap.insertBefore(label, cap.firstChild);
  }
  let text = cap.querySelector(":scope > .rte-caption-text");
  if (!text) {
    text = doc.createElement("span");
    text.className = "rte-caption-text";
    cap.appendChild(text);
  }
  text.setAttribute("contenteditable", "true");
  const align = fig.getAttribute(RTE_CAPTION_ALIGN_ATTR) || "center";
  cap.setAttribute(RTE_CAPTION_ALIGN_ATTR, align);
  const size = captionSizeOf(fig.getAttribute(RTE_CAPTION_SIZE_ATTR));
  cap.setAttribute(RTE_CAPTION_SIZE_ATTR, size);
  return cap;
}

export function applyCaptionSettings(node, { kind, abbr, align, size, custom } = {}) {
  if (!node) return null;
  const fig = node.tagName === "TABLE" ? ensureTableCaptionFigure(node) : node;
  let resolved = kind || fig.getAttribute(RTE_CAPTION_KIND_ATTR) || defaultCaptionKind(fig);
  if (resolved !== "custom" && !CAPTION_LABELS[resolved]) resolved = defaultCaptionKind(fig);
  fig.setAttribute(RTE_CAPTION_KIND_ATTR, resolved);
  if (abbr === true) fig.setAttribute(RTE_CAPTION_ABBR_ATTR, "1");
  else if (abbr === false) fig.removeAttribute(RTE_CAPTION_ABBR_ATTR);
  const nextAlign = ["left", "center", "right"].includes(align)
    ? align
    : (fig.getAttribute(RTE_CAPTION_ALIGN_ATTR) || "center");
  if (nextAlign === "center") fig.removeAttribute(RTE_CAPTION_ALIGN_ATTR);
  else fig.setAttribute(RTE_CAPTION_ALIGN_ATTR, nextAlign);
  const nextSize = captionSizeOf(size || fig.getAttribute(RTE_CAPTION_SIZE_ATTR));
  fig.setAttribute(RTE_CAPTION_SIZE_ATTR, nextSize);
  if (custom != null) {
    const text = String(custom).trim();
    if (text) fig.setAttribute(RTE_CAPTION_CUSTOM_ATTR, text);
    else fig.removeAttribute(RTE_CAPTION_CUSTOM_ATTR);
    if (resolved === "custom" && text) {
      rememberCustomCaptionLabel(fig.closest("[contenteditable='true']") || fig.parentElement, text);
    }
  }
  const cap = ensureCaptionElement(fig);
  cap.setAttribute(RTE_CAPTION_ALIGN_ATTR, nextAlign);
  cap.setAttribute(RTE_CAPTION_SIZE_ATTR, nextSize);
  return fig;
}

export function removeCaption(node) {
  const host = captionHostFor(node);
  if (!host) return;
  host.querySelector(":scope > figcaption.rte-caption")?.remove();
  host.removeAttribute(RTE_CAPTION_KIND_ATTR);
  host.removeAttribute(RTE_CAPTION_ABBR_ATTR);
  host.removeAttribute(RTE_CAPTION_ALIGN_ATTR);
  host.removeAttribute(RTE_CAPTION_SIZE_ATTR);
  host.removeAttribute(RTE_CAPTION_CUSTOM_ATTR);
  if (host.classList.contains("rte-table-figure")) unwrapTableCaptionFigure(host);
}

/** Saved HTML keeps contenteditable on caption text and captioned tables. Read view must not. */
export function readOnlyRichHtml(html) {
  if (!html || typeof document === "undefined") return html || "";
  if (!/contenteditable/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(`<div id="rte-ro">${html}</div>`, "text/html");
  const root = doc.getElementById("rte-ro");
  if (!root) return html;
  root.querySelectorAll(
    "figure.rte-table-figure, figure.rte-table-figure table, figcaption.rte-caption, .rte-caption-label, .rte-caption-text"
  ).forEach((el) => {
    el.setAttribute("contenteditable", "false");
  });
  return root.innerHTML;
}

export function renumberCaptions(root) {
  if (!root?.querySelectorAll) return;
  const counts = { image: 0, figure: 0, table: 0, slides: 0 };
  const customCounts = new Map();
  root.querySelectorAll("figure.rte-image, figure.rte-flow, figure.rte-table-figure").forEach((fig) => {
    const cap = fig.querySelector(":scope > figcaption.rte-caption");
    if (!cap) return;
    const inEditor = !!fig.closest("[contenteditable='true']");
    let kind = fig.getAttribute(RTE_CAPTION_KIND_ATTR);
    if (!kind && fig.classList.contains("rte-table-figure")) kind = "table";
    if (kind !== "custom" && !CAPTION_LABELS[kind]) kind = defaultCaptionKind(fig);
    let word;
    let nextCount;
    if (kind === "custom") {
      word = String(fig.getAttribute(RTE_CAPTION_CUSTOM_ATTR) || "Caption").trim() || "Caption";
      const key = word.toLowerCase();
      nextCount = (customCounts.get(key) || 0) + 1;
      customCounts.set(key, nextCount);
    } else {
      counts[kind] += 1;
      nextCount = counts[kind];
      word = fig.getAttribute(RTE_CAPTION_ABBR_ATTR) === "1"
        ? CAPTION_LABELS[kind].abbr
        : CAPTION_LABELS[kind].full;
    }
    const next = `${word} ${nextCount}.`;
    const size = captionSizeOf(fig.getAttribute(RTE_CAPTION_SIZE_ATTR));
    if (fig.getAttribute(RTE_CAPTION_SIZE_ATTR) !== size) fig.setAttribute(RTE_CAPTION_SIZE_ATTR, size);
    if (cap.getAttribute(RTE_CAPTION_SIZE_ATTR) !== size) cap.setAttribute(RTE_CAPTION_SIZE_ATTR, size);
    let label = cap.querySelector(":scope > .rte-caption-label");
    if (!label) {
      label = fig.ownerDocument.createElement("span");
      label.className = "rte-caption-label";
      cap.insertBefore(label, cap.firstChild);
    }
    if (label.getAttribute("contenteditable") !== "false") label.setAttribute("contenteditable", "false");
    if (label.textContent !== next) label.textContent = next;
    const text = cap.querySelector(":scope > .rte-caption-text");
    const editable = inEditor ? "true" : "false";
    if (text && text.getAttribute("contenteditable") !== editable) {
      text.setAttribute("contenteditable", editable);
    }
    if (cap.getAttribute("contenteditable") !== "false") cap.setAttribute("contenteditable", "false");
    const align = ["left", "center", "right"].includes(fig.getAttribute(RTE_CAPTION_ALIGN_ATTR))
      ? fig.getAttribute(RTE_CAPTION_ALIGN_ATTR)
      : "center";
    if (cap.getAttribute(RTE_CAPTION_ALIGN_ATTR) !== align) cap.setAttribute(RTE_CAPTION_ALIGN_ATTR, align);
    if (fig.classList.contains("rte-table-figure")) {
      fig.draggable = false;
      if (fig.getAttribute("contenteditable") !== "false") fig.setAttribute("contenteditable", "false");
      const table = fig.querySelector(":scope > table");
      const tableEditable = inEditor ? "true" : "false";
      if (table && table.getAttribute("contenteditable") !== tableEditable) {
        table.setAttribute("contenteditable", tableEditable);
      }
      if (table) table.draggable = false;
    }
  });
}

export function figureBodyHtml(fig) {
  const cap = fig.querySelector(":scope > figcaption.rte-caption");
  if (!cap) return fig.innerHTML;
  return [...fig.childNodes]
    .filter((node) => node !== cap)
    .map((node) => (node.nodeType === 1 ? node.outerHTML : node.textContent || ""))
    .join("");
}

export function setFigureBody(fig, html) {
  const cap = fig.querySelector(":scope > figcaption.rte-caption");
  fig.innerHTML = html;
  if (cap) fig.appendChild(cap);
}
